import { audio } from '../systems/audio/AudioSystem.js';

const SELECTED_CLASS = 'menu-button-selected';

/**
 * Global WASD/Enter/Space keyboard navigation for every home-page menu screen.
 * Installed once (see main.js) and driven entirely off the DOM: whatever
 * `.menu-overlay` is currently mounted in #ui-root is the active menu, and
 * every enabled, visible <button> inside it is navigable — so new menus
 * get keyboard support for free, and screens that re-render themselves
 * from scratch on every purchase (the shop pattern) just get re-scanned on
 * the next keypress instead of anyone having to re-register anything.
 *
 * W/S/A/D move the gold highlight spatially (nearest button in that
 * direction, by geometry, wrapping to the far side when the edge is hit);
 * Enter or Space clicks the highlighted button; Escape clicks the screen's Back
 * button (or the modal's Skip/OK). During a run no .menu-overlay exists,
 * so this never eats the game's own WASD movement keys.
 *
 * Mouse hover feeds the same single highlight: hovering a button moves the
 * selection onto it (there's no separate CSS :hover glow), so exactly one
 * button glows at a time no matter how mouse and keyboard are mixed.
 *
 * If a modal is open inside the menu (the summon case reel), navigation is
 * scoped to it so Space can't reach through the backdrop and re-fire the
 * Summon button mid-spin — inside the modal, Space lands on the reel's own
 * Skip (and then OK), which is also why the reel needs no key handling of
 * its own.
 */
export function initMenuKeyNav(uiRoot) {
  let selected = null;

  const activeOverlay = () => {
    const overlays = uiRoot.querySelectorAll('.menu-overlay');
    return overlays[overlays.length - 1] ?? null;
  };

  const scopeOf = (overlay) => overlay.querySelector('.summon-case-overlay') ?? overlay;

  const candidates = (scope) =>
    [...scope.querySelectorAll('button:not(:disabled)')].filter((el) => el.offsetParent !== null);

  const select = (el) => {
    if (selected === el) return;
    selected?.classList.remove(SELECTED_CLASS);
    selected = el;
    if (el) {
      el.classList.add(SELECTED_CLASS);
      el.scrollIntoView({ block: 'nearest' });
    }
  };

  // Default highlight for a freshly-scanned scope, in priority order:
  //
  //  1. An explicit `[data-menu-default]`, for screens whose primary action
  //     is not a `.menu-button` at all. Champion select is the case that
  //     forced this: its champions are `.levelup-card` buttons and the only
  //     `.menu-button` on the screen is **Back**, so rule 3 below used to
  //     light Back on arrival — pressing Enter on the champion screen sent
  //     you backwards, which is as close to an inverted control as a menu
  //     can get. (Locking champions behind Shards makes it worse, not
  //     better: the unlock buttons ARE `.menu-button`s, so the default
  //     would land on "Unlock — 250 Shards".)
  //  2. The primary action, if the screen declares one.
  //  3. The first regular menu button, else any button.
  //
  // A screen opts in by marking one button; nothing else changes.
  const autoSelect = (scope) => {
    const els = candidates(scope);
    select(
      els.find((el) => el.hasAttribute('data-menu-default'))
        ?? els.find((el) => el.classList.contains('menu-button-primary'))
        ?? els.find((el) => el.classList.contains('menu-button'))
        ?? els[0]
        ?? null
    );
  };

  // Nearest button whose center lies in the pressed direction, scored by
  // distance along that axis plus a heavier penalty for sideways offset
  // (standard spatial-nav weighting, keeps W/S from jumping across
  // columns). If nothing lies that way, wrap to the farthest button on the
  // opposite side with the same sideways penalty.
  const pickNext = (from, els, [vx, vy]) => {
    const center = (r) => [r.left + r.width / 2, r.top + r.height / 2];
    const [fx, fy] = center(from.getBoundingClientRect());
    let best = null;
    let bestScore = Infinity;
    let wrap = null;
    let wrapScore = -Infinity;
    for (const el of els) {
      if (el === from) continue;
      const [x, y] = center(el.getBoundingClientRect());
      const forward = (x - fx) * vx + (y - fy) * vy;
      const sideways = Math.abs((x - fx) * vy) + Math.abs((y - fy) * vx);
      if (forward > 1) {
        const score = forward + sideways * 2.5;
        if (score < bestScore) { bestScore = score; best = el; }
      } else if (forward < -1) {
        const score = -forward - sideways * 2.5;
        if (score > wrapScore) { wrapScore = score; wrap = el; }
      }
    }
    return best ?? wrap;
  };

  const DIRECTIONS = { w: [0, -1], s: [0, 1], a: [-1, 0], d: [1, 0] };

  window.addEventListener('keydown', (e) => {
    // Don't hijack typing in the login/redeem-code text fields.
    const tag = e.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    const overlay = activeOverlay();
    if (!overlay) return;
    const key = e.key.toLowerCase();
    const dir = DIRECTIONS[key];
    if (!dir && e.key !== ' ' && e.key !== 'Enter' && e.key !== 'Escape') return;

    const scope = scopeOf(overlay);
    const els = candidates(scope);
    if (els.length === 0) return;

    // Escape = the screen's Back button, regardless of what's highlighted.
    // Inside the summon modal (which has no Back) it maps to the dismissive
    // action instead: Skip while the reel spins, OK on the reveal.
    if (e.key === 'Escape') {
      const target =
        els.find((el) => el.dataset.action === 'skip')
          ?? els.find((el) => el.textContent.trim() === 'OK')
          ?? els.find((el) => el.textContent.trim() === 'Back');
      if (target) {
        audio.playSfx('click');
        target.click();
      }
      return;
    }

    // A stale highlight (menu re-rendered itself, or a modal opened/closed)
    // resets to the scope's default before the key applies.
    if (!selected || !els.includes(selected)) {
      autoSelect(scope);
      if (dir) return; // this press just (re)establishes the highlight
    }

    if (dir) {
      const next = pickNext(selected, els, dir);
      if (next && next !== selected) {
        audio.playSfx('click');
        select(next);
      }
    } else {
      // preventDefault stops both page scroll and the browser's native
      // Prevent the browser's native button activation so a still-focused,
      // previously-clicked button can't double-fire alongside the
      // highlighted one.
      e.preventDefault();
      selected.click();
    }
  });

  // Hovering a navigable button moves the one shared highlight onto it —
  // mouse and keyboard drive the same selection, so exactly one button
  // glows at a time (there is deliberately no separate :hover glow).
  //
  // Only real pointer movement counts: when a new screen mounts (or
  // scrolls) under a stationary cursor, the browser synthesizes a
  // mouseover for whatever now sits there, which would yank the highlight
  // away from someone navigating purely by keyboard. Synthetic re-hovers
  // reuse the cursor's last coordinates, while genuinely hovering a new
  // button always involves movement — so same-coordinate events are
  // ignored.
  let lastPointer = null;
  uiRoot.addEventListener('mouseover', (e) => {
    const moved = !lastPointer || lastPointer.x !== e.clientX || lastPointer.y !== e.clientY;
    lastPointer = { x: e.clientX, y: e.clientY };
    if (!moved) return;
    const overlay = activeOverlay();
    if (!overlay) return;
    const scope = scopeOf(overlay);
    const button = e.target.closest?.('button');
    if (!button || button.disabled || !scope.contains(button)) return;
    select(button);
  });

  // Re-establish the default highlight whenever the mounted screen (or a
  // modal within it) changes, so menus open with a visible selection
  // instead of waiting for the first keypress. childList-only: select()
  // toggles classes, which this doesn't observe, so no feedback loop.
  const observer = new MutationObserver(() => {
    const overlay = activeOverlay();
    if (!overlay) { select(null); return; }
    const scope = scopeOf(overlay);
    if (!selected || !candidates(scope).includes(selected)) autoSelect(scope);
  });
  observer.observe(uiRoot, { childList: true, subtree: true });
}
